import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '../../components/ui';
import { useKnowledgeBase } from '../../contexts/KnowledgeBaseContext';
import { useTheme } from '../../contexts/ThemeContext';
import type { ArticleCategory, ArticleFilters } from '../../types/knowledgeBase';
import { ArticleCard } from './components/ArticleCard';
import { ArticleSearch } from './components/ArticleSearch';
import { CategoryChips } from './components/CategoryChips';
import './KnowledgeBase.css';

// Current user role - hardcoded for now, will come from auth
const CURRENT_USER_ROLE = 'T2 Tech'; // or 'Admin' or 'T1 Tech'

function canCreateArticles(role: string): boolean {
  return role === 'Admin' || role === 'T2 Tech';
}

export function KnowledgeBase() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { articles } = useKnowledgeBase();

  const [filters, setFilters] = useState<ArticleFilters>({
    searchQuery: '',
    categories: [],
    articleTypes: [],
    audiences: [],
  });

  // Filter and search articles
  const filteredArticles = useMemo(() => {
    return articles
      .filter((article) => article.isPublished)
      .filter((article) => {
        // Category filter
        if (filters.categories.length > 0 && !filters.categories.includes(article.category)) {
          return false;
        }

        // Search filter
        if (filters.searchQuery) {
          const query = filters.searchQuery.toLowerCase();

          // Search in title
          if (article.title.toLowerCase().includes(query)) return true;

          // Search in description
          if (article.description.toLowerCase().includes(query)) return true;

          // Search in tags
          if (article.tags.some((tag) => tag.toLowerCase().includes(query))) return true;

          // Search in step titles and content
          if (
            article.steps.some(
              (step) =>
                step.title.toLowerCase().includes(query) ||
                step.content.toLowerCase().includes(query)
            )
          ) {
            return true;
          }

          return false;
        }

        return true;
      })
      .sort((a, b) => {
        // Sort by most recently updated
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });
  }, [articles, filters]);

  const handleSearchChange = useCallback((query: string) => {
    setFilters((prev) => ({ ...prev, searchQuery: query }));
  }, []);

  const handleCategoryChange = useCallback((categories: ArticleCategory[]) => {
    setFilters((prev) => ({ ...prev, categories }));
  }, []);

  const handleArticleClick = useCallback(
    (articleId: string) => {
      navigate(`/knowledge-base/article/${articleId}`);
    },
    [navigate]
  );

  const handleNewArticle = useCallback(() => {
    navigate('/knowledge-base/new');
  }, [navigate]);

  const showCreateButton = canCreateArticles(CURRENT_USER_ROLE);

  return (
    <Box className="knowledge-base" data-theme={theme}>
      <Box className="knowledge-base__container">
        <Box className="knowledge-base__header">
          <Box className="knowledge-base__header-content">
            <Typography variant="h5" component="h1" className="knowledge-base__title">
              Knowledge Base
              <span className="knowledge-base__count">{filteredArticles.length}</span>
            </Typography>
            <Typography variant="body2" className="knowledge-base__subtitle">
              Quick reference guides for IT support
            </Typography>
          </Box>
          {showCreateButton && (
            <button className="knowledge-base__new-button" onClick={handleNewArticle}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 3v10M3 8h10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              New Article
            </button>
          )}
        </Box>

        <Box className="knowledge-base__filters">
          <ArticleSearch value={filters.searchQuery} onChange={handleSearchChange} />
          <CategoryChips
            selectedCategories={filters.categories}
            onChange={handleCategoryChange}
          />
        </Box>

        <Box className="knowledge-base__results">
          <Typography variant="body2" color="text.secondary">
            Showing {filteredArticles.length} {filteredArticles.length === 1 ? 'article' : 'articles'}
            {filters.searchQuery && ` for "${filters.searchQuery}"`}
          </Typography>
        </Box>

        {filteredArticles.length === 0 ? (
          <Box className="knowledge-base__empty">
            <Box className="knowledge-base__empty-icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 8h24a4 4 0 014 4v24a4 4 0 01-4 4H12a4 4 0 01-4-4V12a4 4 0 014-4z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16 16h16M16 24h16M16 32h8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </Box>
            <Typography variant="h6" className="knowledge-base__empty-title">
              No articles found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filters.searchQuery
                ? 'Try adjusting your search terms or clearing filters'
                : 'No articles have been published yet'}
            </Typography>
          </Box>
        ) : (
          <Box className="knowledge-base__grid">
            {filteredArticles.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                onClick={() => handleArticleClick(article.id)}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
